import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { AdminLayout } from '../components/AdminLayout.js';
import { SurchargeLadderForm } from '../components/forms/SurchargeLadderForm.js';
import { SurchargeRuleSetForm } from '../components/forms/SurchargeRuleSetForm.js';
import { egLabel, formatLadderSteps } from '../lib/surcharge-ladder-form.js';
import { Button } from '../components/ui/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table.js';
import { requireRole } from '../lib/auth-guard.js';
import { trpc } from '../lib/trpc.js';

const EMPTY_UUID = '00000000-0000-4000-8000-000000000000';
const NO_TIMESHEET = '__none__';

export const Route = createFileRoute('/admin/surcharge-rule-sets')({
  component: AdminSurchargeRuleSets,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['ceo', 'internal_staff']);
  },
});

type BaseFilter = 'all' | 'wage' | 'bill_rate';

function baseLabel(base: 'wage' | 'bill_rate'): string {
  return base === 'wage' ? 'Worker-Lohn' : 'Verrechnungssatz';
}

function stackingLabel(policy: 'cumulative' | 'highest_only'): string {
  return policy === 'cumulative' ? 'addieren' : 'nur höchster';
}

function formatMinutes(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`;
}

function formatCents(cents: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function conditionLabel(kind: string, payload: unknown): string {
  const record = asRecord(payload);
  if (kind === 'weekdays') {
    const labels = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    const days = Array.isArray(record?.days) ? record.days : [];
    return `Wochentage: ${days.map((day) => labels[Number(day)] ?? String(day)).join(', ')}`;
  }
  if (kind === 'holiday') return 'gesetzlicher Feiertag';
  if (kind === 'special_days') {
    const days = Array.isArray(record?.days) ? record.days : [];
    const formatted = days.map((value) => {
      const day = asRecord(value);
      if (!day) return '—';
      const date = `${String(day.month).padStart(2, '0')}-${String(day.day).padStart(2, '0')}`;
      return day.fromLocalTime ? `${date} ab ${day.fromLocalTime}` : date;
    });
    return `Sondertage: ${formatted.join(', ')}`;
  }
  return 'immer';
}

function AdminSurchargeRuleSets() {
  const [baseFilter, setBaseFilter] = useState<BaseFilter>('all');
  const [creating, setCreating] = useState(false);
  const [creatingLadder, setCreatingLadder] = useState(false);
  const [selectedRuleSetId, setSelectedRuleSetId] = useState<string | null>(null);
  const [selectedTimesheetId, setSelectedTimesheetId] = useState<string | null>(null);

  const clients = trpc.clients.list.useQuery({ limit: 500 });
  const ladders = trpc.surchargeLadders.list.useQuery({});
  const timesheets = trpc.timesheets.list.useQuery({});
  const ruleSets = trpc.surchargeRuleSets.list.useQuery(
    baseFilter === 'all' ? {} : { base: baseFilter },
  );
  const details = trpc.surchargeRuleSets.byId.useQuery(
    { id: selectedRuleSetId ?? EMPTY_UUID },
    { enabled: selectedRuleSetId != null, retry: false },
  );
  const preview = trpc.surchargeRuleSets.previewForTimesheet.useQuery(
    { timesheetId: selectedTimesheetId ?? EMPTY_UUID },
    { enabled: selectedTimesheetId != null, retry: false },
  );

  const clientNames = useMemo(
    () => new Map(clients.data?.map((client) => [client.id, client.companyName ?? '(unbenannt)'])),
    [clients.data],
  );

  return (
    <AdminLayout>
      <div className="space-y-5">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold">Zuschlags-Regelwerke</h1>
            <p className="max-w-4xl text-[var(--muted-foreground)]">
              Versionierte Lohn- und Kundenregeln verwalten und gegen echte Tageszeilen prüfen.
              Änderungen erzeugen neue Versionen; die Vorschau schreibt keine Abrechnungsdaten.
            </p>
          </div>
          <Button
            variant={creating ? 'ghost' : 'default'}
            size="sm"
            onClick={() => setCreating((value) => !value)}
          >
            {creating ? 'Editor schließen' : '+ Neue Version'}
          </Button>
        </header>

        {creating && (
          <Card>
            <CardHeader>
              <CardTitle>Neue Regelwerk-Version</CardTitle>
              <CardDescription>
                Eine Version mit demselben Namen schließt den zeitlich vorhergehenden Stand
                automatisch zum Vortag. Bestehende Versionen bleiben unverändert erhalten.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SurchargeRuleSetForm
                clients={clients.data ?? []}
                onCreated={(id) => {
                  setSelectedRuleSetId(id);
                  setCreating(false);
                }}
              />
            </CardContent>
          </Card>
        )}

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1.25fr)_minmax(22rem,0.75fr)]">
          <Card>
            <CardHeader className="gap-3 md:flex-row md:items-center md:justify-between md:space-y-0">
              <div>
                <CardTitle>Versionen</CardTitle>
                <CardDescription>Neueste Version je Name erscheint zuerst.</CardDescription>
              </div>
              <Select value={baseFilter} onValueChange={(value) => setBaseFilter(value as BaseFilter)}>
                <SelectTrigger className="w-full md:w-56" aria-label="Berechnungsbasis filtern">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Basen</SelectItem>
                  <SelectItem value="wage">Worker-Lohn</SelectItem>
                  <SelectItem value="bill_rate">Verrechnungssatz</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Basis / Scope</TableHead>
                    <TableHead>Gültigkeit</TableHead>
                    <TableHead>Stacking</TableHead>
                    <TableHead className="text-right">Aktion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ruleSets.isLoading && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-[var(--muted-foreground)]">
                        Lade Regelwerke …
                      </TableCell>
                    </TableRow>
                  )}
                  {ruleSets.error && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-[oklch(0.55_0.2_25)]">
                        {ruleSets.error.message}
                      </TableCell>
                    </TableRow>
                  )}
                  {ruleSets.data?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-[var(--muted-foreground)]">
                        Noch keine Regelwerke für diesen Filter.
                      </TableCell>
                    </TableRow>
                  )}
                  {ruleSets.data?.map((ruleSet) => (
                    <TableRow key={ruleSet.id}>
                      <TableCell className="font-medium">{ruleSet.name}</TableCell>
                      <TableCell>
                        <div>{baseLabel(ruleSet.base)}</div>
                        <div className="text-xs text-[var(--muted-foreground)]">
                          {ruleSet.clientId
                            ? (clientNames.get(ruleSet.clientId) ?? 'Kundenspezifisch')
                            : 'Global'}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {ruleSet.validFrom} – {ruleSet.validTo ?? 'offen'}
                      </TableCell>
                      <TableCell>{stackingLabel(ruleSet.stackingPolicy)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          size="sm"
                          variant={selectedRuleSetId === ruleSet.id ? 'default' : 'outline'}
                          onClick={() => setSelectedRuleSetId(ruleSet.id)}
                        >
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Versionsdetails</CardTitle>
              <CardDescription>Read-only: Korrekturen immer als neue Version anlegen.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!selectedRuleSetId && (
                <p className="text-sm text-[var(--muted-foreground)]">
                  Wähle eine Version aus der Liste.
                </p>
              )}
              {details.isLoading && <p className="text-sm">Lade Details …</p>}
              {details.error && (
                <p className="text-sm text-[oklch(0.55_0.2_25)]">{details.error.message}</p>
              )}
              {details.data && (
                <>
                  <dl className="grid grid-cols-[9rem_1fr] gap-x-3 gap-y-2 text-sm">
                    <dt className="text-[var(--muted-foreground)]">Name</dt>
                    <dd className="font-medium">{details.data.name}</dd>
                    <dt className="text-[var(--muted-foreground)]">Basis</dt>
                    <dd>{baseLabel(details.data.base)}</dd>
                    <dt className="text-[var(--muted-foreground)]">Scope</dt>
                    <dd>
                      {details.data.clientId
                        ? (clientNames.get(details.data.clientId) ?? 'Kundenspezifisch')
                        : 'Global'}
                    </dd>
                    <dt className="text-[var(--muted-foreground)]">Gültig</dt>
                    <dd>
                      {details.data.validFrom} – {details.data.validTo ?? 'offen'}
                    </dd>
                    <dt className="text-[var(--muted-foreground)]">Stacking</dt>
                    <dd>{stackingLabel(details.data.stackingPolicy)}</dd>
                    <dt className="text-[var(--muted-foreground)]">Notiz</dt>
                    <dd>{details.data.notes ?? '—'}</dd>
                  </dl>

                  <div className="space-y-2">
                    <h3 className="font-semibold">Regeln ({details.data.rules.length})</h3>
                    {details.data.rules.map((rule) => (
                      <div key={rule.id} className="rounded-md border border-[var(--border)] p-3 text-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <code>{rule.key}</code>
                          <strong>{Number(rule.percent).toLocaleString('de-DE')} %</strong>
                        </div>
                        <p className="mt-1 text-[var(--muted-foreground)]">
                          {conditionLabel(rule.conditionKind, rule.conditionDays)}
                          {rule.windowFrom && rule.windowTo
                            ? ` · ${rule.windowFrom}–${rule.windowTo}`
                            : ''}
                          {rule.onlyIfShiftStartedBeforeMidnight
                            ? ' · nur bei Schichtstart vor Mitternacht'
                            : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="gap-3 md:flex-row md:items-center md:justify-between md:space-y-0">
            <div>
              <CardTitle>Staffeln (nach Einsatzdauer)</CardTitle>
              <CardDescription>
                %-Treppen nach ununterbrochener Einsatzdauer beim Kunden (Unterbrechungen bis 3
                Monate werden angerechnet) — z. B. Preis-Staffeln im Kundenvertrag.
              </CardDescription>
            </div>
            <Button
              variant={creatingLadder ? 'ghost' : 'default'}
              size="sm"
              onClick={() => setCreatingLadder((value) => !value)}
            >
              {creatingLadder ? 'Editor schließen' : '+ Neue Staffel-Version'}
            </Button>
          </CardHeader>
          <CardContent className="space-y-5">
            {creatingLadder && (
              <SurchargeLadderForm
                clients={clients.data ?? []}
                onCreated={() => setCreatingLadder(false)}
              />
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Basis / Scope</TableHead>
                  <TableHead>Gültigkeit</TableHead>
                  <TableHead>Treppe</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ladders.isLoading && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-[var(--muted-foreground)]">
                      Lade Staffeln …
                    </TableCell>
                  </TableRow>
                )}
                {ladders.error && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-[oklch(0.55_0.2_25)]">
                      {ladders.error.message}
                    </TableCell>
                  </TableRow>
                )}
                {ladders.data?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-[var(--muted-foreground)]">
                      Noch keine Staffeln hinterlegt.
                    </TableCell>
                  </TableRow>
                )}
                {ladders.data?.map((ladder) => (
                  <LadderRow key={ladder.id} ladderId={ladder.id} ladder={ladder} clientNames={clientNames} />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Timesheet-Vorschau</CardTitle>
            <CardDescription>
              Berechnet aktive Regelwerke am Periodenstart auf den echten Tageszeilen. Das Ergebnis
              ist read-only und wird weder am Stundenzettel noch in der Abrechnung gespeichert.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-72 flex-1 space-y-1.5">
                <label htmlFor="preview-timesheet" className="text-sm font-medium">
                  Stundenzettel
                </label>
                <Select
                  value={selectedTimesheetId ?? NO_TIMESHEET}
                  onValueChange={(value) =>
                    setSelectedTimesheetId(value === NO_TIMESHEET ? null : value)
                  }
                >
                  <SelectTrigger id="preview-timesheet">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_TIMESHEET}>Stundenzettel auswählen …</SelectItem>
                    {timesheets.data?.map((timesheet) => (
                      <SelectItem key={timesheet.id} value={timesheet.id}>
                        {[timesheet.workerFirstName, timesheet.workerLastName]
                          .filter(Boolean)
                          .join(' ') || 'Worker'}{' '}
                        · {timesheet.periodStart}–{timesheet.periodEnd} ·{' '}
                        {timesheet.clientCompanyName ?? 'ohne Kunde'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                variant="outline"
                disabled={!selectedTimesheetId || preview.isFetching}
                onClick={() => void preview.refetch()}
              >
                {preview.isFetching ? 'Berechne …' : 'Neu berechnen'}
              </Button>
            </div>

            {timesheets.error && (
              <p className="text-sm text-[oklch(0.55_0.2_25)]">{timesheets.error.message}</p>
            )}
            {preview.error && (
              <div className="rounded-md border border-[oklch(0.55_0.2_25)] p-3 text-sm">
                <strong>Vorschau nicht möglich:</strong> {preview.error.message}
              </div>
            )}
            {preview.data && (
              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                  <PreviewMetric label="Arbeitszeit" value={formatMinutes(preview.data.totalWorkMinutes)} />
                  <PreviewMetric label="Pausen" value={formatMinutes(preview.data.totalBreakMinutes)} />
                  <PreviewMetric label="Netto" value={formatMinutes(preview.data.totalNetMinutes)} />
                  <PreviewMetric label="Zeitzone" value={preview.data.timezone} />
                  <PreviewMetric label="Bundesland" value={preview.data.worksiteState} />
                </div>

                {preview.data.ladderResults.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-[var(--muted-foreground)]">
                      Anrechenbare Einsatzdauer am Periodenstart:{' '}
                      <strong>{preview.data.tenure.completedMonths} Monate</strong> (
                      {preview.data.tenure.completedWeeks} Wochen /{' '}
                      {preview.data.tenure.totalDays} Tage, Einsätze beim selben Kunden verkettet,
                      Lücken ≤ 3 Monate angerechnet).
                    </p>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Staffel</TableHead>
                          <TableHead>Basis</TableHead>
                          <TableHead>Aktive Stufe</TableHead>
                          <TableHead className="text-right">Staffel-Zuschlag</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {preview.data.ladderResults.map((entry) => (
                          <TableRow key={entry.ladderId}>
                            <TableCell className="font-medium">{entry.name}</TableCell>
                            <TableCell>{baseLabel(entry.base)}</TableCell>
                            <TableCell>
                              {entry.skippedReason ??
                                (entry.percent != null ? `${entry.percent} %` : '—')}
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {entry.amountCents != null ? formatCents(entry.amountCents) : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Regelwerk</TableHead>
                      <TableHead>Basis</TableHead>
                      <TableHead>Stacking</TableHead>
                      <TableHead>Treffer</TableHead>
                      <TableHead className="text-right">Zuschlag</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.data.results.map((entry) => (
                      <TableRow key={entry.ruleSetId}>
                        <TableCell className="font-medium">{entry.name}</TableCell>
                        <TableCell>{baseLabel(entry.base)}</TableCell>
                        <TableCell>
                          {entry.result ? stackingLabel(entry.result.stackingPolicy) : '—'}
                        </TableCell>
                        <TableCell>
                          {entry.skippedReason ?? `${entry.result?.surcharges.length ?? 0} angewandt`}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {entry.result ? formatCents(entry.result.totalSurchargeCents) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {preview.data.results.some((entry) => entry.result?.surcharges.length) && (
                  <details className="rounded-md border border-[var(--border)] p-3">
                    <summary className="cursor-pointer font-medium">Berechnungsdetails</summary>
                    <div className="mt-3 space-y-4">
                      {preview.data.results.map((entry) =>
                        entry.result?.surcharges.length ? (
                          <div key={entry.ruleSetId}>
                            <h3 className="mb-2 font-semibold">{entry.name}</h3>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Regel</TableHead>
                                  <TableHead>Topf</TableHead>
                                  <TableHead>Minuten</TableHead>
                                  <TableHead>Prozent</TableHead>
                                  <TableHead className="text-right">Betrag</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {entry.result.surcharges.map((surcharge, index) => (
                                  <TableRow key={`${surcharge.bucketKey}-${surcharge.ruleKey}-${index}`}>
                                    <TableCell>{surcharge.ruleKey}</TableCell>
                                    <TableCell className="font-mono text-xs">
                                      {surcharge.bucketKey}
                                    </TableCell>
                                    <TableCell>{surcharge.minutes}</TableCell>
                                    <TableCell>{surcharge.percent} %</TableCell>
                                    <TableCell className="text-right">
                                      {formatCents(surcharge.amountCents)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ) : null,
                      )}
                    </div>
                  </details>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

function LadderRow({
  ladderId,
  ladder,
  clientNames,
}: {
  ladderId: string;
  ladder: {
    name: string;
    base: 'wage' | 'bill_rate';
    clientId: string | null;
    entgeltGroups: string[] | null;
    validFrom: string;
    validTo: string | null;
  };
  clientNames: Map<string, string>;
}) {
  // Stufen kommen aus dem Detail-Endpoint; bei Staging-Mengen (wenige
  // Staffeln) ist eine Query je Zeile unkritisch.
  const details = trpc.surchargeLadders.byId.useQuery({ id: ladderId });
  return (
    <TableRow>
      <TableCell className="font-medium">{ladder.name}</TableCell>
      <TableCell>
        <div>{baseLabel(ladder.base)}</div>
        <div className="text-xs text-[var(--muted-foreground)]">
          {ladder.clientId ? (clientNames.get(ladder.clientId) ?? 'Kundenspezifisch') : 'Global'}
          {ladder.entgeltGroups?.length
            ? ` · ${ladder.entgeltGroups.map(egLabel).join(', ')}`
            : ' · alle EG'}
        </div>
      </TableCell>
      <TableCell className="text-sm">
        {ladder.validFrom} – {ladder.validTo ?? 'offen'}
      </TableCell>
      <TableCell className="text-sm">
        {details.data ? formatLadderSteps(details.data.steps) : '…'}
      </TableCell>
    </TableRow>
  );
}

function PreviewMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-[var(--border)] p-3">
      <div className="text-xs text-[var(--muted-foreground)]">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  );
}
